import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Mail, Phone, User, Trash2, Pencil, Loader2, UserPlus, History, Headset } from "lucide-react";
import { formatDateTime } from "../lib/format";
import { formatApiErrorDetail } from "../lib/api";

const AUDIT_LABELS = {
  crea_account: ["Creato account", "text-emerald-700 bg-emerald-50"],
  modifica_account: ["Modificato account", "text-sky-700 bg-sky-50"],
  cambia_ruolo: ["Cambiato ruolo", "text-violet-700 bg-violet-50"],
  attiva_account: ["Attivato", "text-emerald-700 bg-emerald-50"],
  disattiva_account: ["Disattivato", "text-amber-700 bg-amber-50"],
  elimina_account: ["Eliminato account", "text-red-700 bg-red-50"],
};

export default function StaffPage() {
  const [staff, setStaff] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", phone: "", operatore_co: "", password: "", staff_role: "operatore" });

  const load = useCallback(async () => {
    const { data } = await api.get("/staff");
    setStaff(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const [audit, setAudit] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const loadAudit = useCallback(async () => {
    try { const { data } = await api.get("/staff/audit"); setAudit(data); } catch { setAudit([]); }
  }, []);
  useEffect(() => { if (auditOpen) loadAudit(); }, [auditOpen, loadAudit]);
  const refreshAudit = () => { if (auditOpen) loadAudit(); };

  const changeRole = async (s, staff_role) => {
    try {
      await api.patch(`/staff/${s.id}/role`, { staff_role });
      setStaff((prev) => prev.map((x) => (x.id === s.id ? { ...x, staff_role } : x)));
      refreshAudit();
      toast.success(`${s.name}: ruolo ${staff_role === "amministratore" ? "Amministratore" : "Operatore"}`);
    } catch (err) {
      toast.error("Ruolo non aggiornato", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/staff/${s.id}`);
      await load(); refreshAudit();
      toast.success(s.active ? "Account disattivato" : "Account riattivato");
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const openEdit = (s) => {
    setEditForm({ id: s.id, name: s.name || "", email: s.email || "", phone: s.phone || "", operatore_co: s.operatore_co || "" });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const { id, ...payload } = editForm;
      await api.put(`/staff/${id}`, payload);
      toast.success("Account aggiornato");
      setEditOpen(false);
      setEditForm(null);
      await load(); refreshAudit();
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setEditSaving(false); }
  };

  const saveCreate = async (e) => {
    e.preventDefault();
    setCreateSaving(true);
    try {
      await api.post("/staff", createForm);
      toast.success("Operatore aggiunto");
      setCreateOpen(false);
      setCreateForm({ name: "", email: "", phone: "", operatore_co: "", password: "", staff_role: "operatore" });
      await load(); refreshAudit();
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setCreateSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Eliminare definitivamente l'account "${s.name}"? L'operazione è irreversibile.`)) return;
    try {
      await api.delete(`/staff/${s.id}`);
      await load(); refreshAudit();
      toast.success("Account eliminato");
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto nx-fade-in">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
              <ShieldCheck className="h-7 w-7 text-primary" /> Anagrafica Staff
            </h1>
            <p className="text-sm text-slate-500 mt-1">Account della Centrale Operativa. Disattiva per revocare l'accesso.</p>
          </div>
          <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) setCreateForm({ name: "", email: "", phone: "", operatore_co: "", password: "", staff_role: "operatore" }); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-staff-button" className="gap-2"><UserPlus className="h-4 w-4" /> Nuovo Operatore</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Nuovo Operatore</DialogTitle>
                <DialogDescription>Crea un account per la Centrale Operativa. Potrà accedere subito con queste credenziali.</DialogDescription>
              </DialogHeader>
              <form onSubmit={saveCreate} className="space-y-4 pt-2">
                <div>
                  <Label className="nx-label">Nome e Cognome</Label>
                  <Input data-testid="staff-create-name" required className="mt-1.5"
                    value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Email</Label>
                  <Input data-testid="staff-create-email" type="email" required className="mt-1.5"
                    value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Telefono</Label>
                  <Input data-testid="staff-create-phone" className="mt-1.5"
                    value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Operatore C.O.</Label>
                  <Input data-testid="staff-create-operatore-co" className="mt-1.5" placeholder="Es. Mario R. — precompila il Referente Operativo Nexus"
                    value={createForm.operatore_co} onChange={(e) => setCreateForm({ ...createForm, operatore_co: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Ruolo</Label>
                  <Select value={createForm.staff_role} onValueChange={(v) => setCreateForm({ ...createForm, staff_role: v })}>
                    <SelectTrigger data-testid="staff-create-role" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="amministratore">Amministratore</SelectItem>
                      <SelectItem value="operatore">Operatore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="nx-label">Password</Label>
                  <Input data-testid="staff-create-password" type="password" required minLength={6} className="mt-1.5"
                    value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="staff-create-save" disabled={createSaving} className="gap-2 w-full">
                    {createSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiungi Operatore"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="border-slate-200 overflow-hidden">
          {staff === null ? (
            <div className="p-5 space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-12 w-full" />)}</div>
          ) : staff.length === 0 ? (
            <div className="py-16 text-center">
              <User className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">Nessun account staff registrato.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Operatore</TableHead>
                  <TableHead>Contatti</TableHead>
                  <TableHead>Ruolo</TableHead>
                  <TableHead>Attivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((s) => (
                  <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                    <TableCell>
                      <p className="font-medium text-slate-800 text-sm flex items-center gap-2">
                        {s.name}
                        {s.is_self && <span className="text-[10px] font-semibold text-primary bg-primary/10 rounded-full px-2 py-0.5">Tu</span>}
                      </p>
                      <p className="text-xs text-slate-400">{s.active ? "Attivo" : "Disattivato"}</p>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" /> {s.email}</span>
                      {s.phone && <span className="flex items-center gap-1.5 mt-0.5"><Phone className="h-3.5 w-3.5 text-slate-400" /> {s.phone}</span>}
                      {s.operatore_co && <span data-testid={`staff-operatore-co-${s.id}`} className="flex items-center gap-1.5 mt-0.5 text-xs text-primary"><Headset className="h-3.5 w-3.5" /> Operatore C.O.: {s.operatore_co}</span>}
                    </TableCell>
                    <TableCell>
                      <Select value={s.staff_role} onValueChange={(v) => changeRole(s, v)} disabled={s.is_self}>
                        <SelectTrigger data-testid={`staff-role-${s.id}`} className={`h-8 w-40 text-xs font-semibold ${s.staff_role === "amministratore" ? "bg-primary/10 text-primary border-primary/30" : "bg-slate-100 text-slate-700"}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="amministratore">Amministratore</SelectItem>
                          <SelectItem value="operatore">Operatore</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Switch data-testid={`staff-toggle-${s.id}`} checked={s.active} disabled={s.is_self}
                          onCheckedChange={() => toggle(s)} />
                        <Button data-testid={`staff-edit-${s.id}`} variant="ghost" size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-primary"
                          title="Modifica account" onClick={() => openEdit(s)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button data-testid={`staff-delete-${s.id}`} variant="ghost" size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-red-600" disabled={s.is_self}
                          title="Elimina account" onClick={() => remove(s)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) setEditForm(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Modifica Account Staff</DialogTitle>
              <DialogDescription>Aggiorna i dati dell'operatore della Centrale Operativa.</DialogDescription>
            </DialogHeader>
            {editForm && (
              <form onSubmit={saveEdit} className="space-y-4 pt-2">
                <div>
                  <Label className="nx-label">Nome</Label>
                  <Input data-testid="staff-edit-name" required className="mt-1.5"
                    value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Email</Label>
                  <Input data-testid="staff-edit-email" type="email" required className="mt-1.5"
                    value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Telefono</Label>
                  <Input data-testid="staff-edit-phone" className="mt-1.5"
                    value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Operatore C.O.</Label>
                  <Input data-testid="staff-edit-operatore-co" className="mt-1.5" placeholder="Es. Mario R. — precompila il Referente Operativo Nexus"
                    value={editForm.operatore_co} onChange={(e) => setEditForm({ ...editForm, operatore_co: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="staff-edit-save" disabled={editSaving} className="gap-2 w-full">
                    {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva Modifiche"}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <Card className="border-slate-200 mt-8 overflow-hidden" data-testid="staff-audit-card">
          <button type="button" data-testid="staff-audit-toggle" onClick={() => setAuditOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-5 py-4 text-left hover:bg-slate-50">
            <History className="h-5 w-5 text-primary" />
            <span className="font-semibold text-slate-800">Log azioni Staff</span>
            <span className="text-xs text-slate-400 ml-1">chi ha creato, modificato, disattivato o cambiato ruolo</span>
            <span className="ml-auto text-xs text-primary">{auditOpen ? "Nascondi" : "Mostra"}</span>
          </button>
          {auditOpen && (
            audit === null ? <div className="p-5"><Skeleton className="h-10 w-full" /></div>
            : audit.length === 0 ? <p className="px-5 pb-5 text-sm text-slate-500">Nessuna azione registrata.</p>
            : (
              <ul className="divide-y divide-slate-100 max-h-96 overflow-auto" data-testid="staff-audit-list">
                {audit.map((a) => {
                  const [label, cls] = AUDIT_LABELS[a.action] || [a.action, "text-slate-700 bg-slate-100"];
                  return (
                    <li key={a.id} data-testid="staff-audit-row" className="px-5 py-2.5 text-sm flex flex-wrap items-center gap-2">
                      <span className="text-xs text-slate-400 w-32 shrink-0">{formatDateTime(a.at)}</span>
                      <span className="font-medium text-slate-800">{a.by_name}</span>
                      <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${cls}`}>{label}</span>
                      {a.target_name && <span className="text-slate-700">{a.target_name}</span>}
                      {a.details && <span className="text-xs text-slate-500">· {a.details}</span>}
                    </li>
                  );
                })}
              </ul>
            )
          )}
        </Card>
      </div>
    </AppLayout>
  );
}
